import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "../lib/api";
import type { OutreachGate, OutreachGateState, OutreachStatus } from "../lib/types";
import { useScope } from "../scope/ScopeContext";

/**
 * The state of the outreach machine, in one line, on every outreach screen.
 *
 * Six locks stand between a stored customer and a real message (sender, campaign
 * mode, row approval, consent/compliance, A2P registration, pacing window). All
 * six were enforced on the server and none were visible where a human clicks —
 * so the queue looked broken when it was merely holding the line. This renders
 * the server's verdicts; it computes nothing itself.
 *
 * The headline is the part that matters. It answers the question an operator
 * actually has ("if I approve this person, what happens to them?") rather than
 * listing settings and leaving them to do the inference.
 */

const DOT: Record<OutreachGateState, string> = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  blocked: "bg-rose-500",
  idle: "bg-slate-300",
};

const HEADLINE_TONE: Record<"safe" | "live" | "stopped", string> = {
  // Nothing will leave — the reassuring case, stated so nobody hesitates to approve.
  safe: "border-slate-200 bg-slate-50 text-slate-700",
  // Real messages are moving right now. This is the one that should catch an eye.
  live: "border-emerald-200 bg-emerald-50 text-emerald-900",
  // Something is misconfigured badly enough that nothing can ever send.
  stopped: "border-amber-200 bg-amber-50 text-amber-900",
};

function tone(status: OutreachStatus): "safe" | "live" | "stopped" {
  if (status.anythingCanSend && !status.approvalIsSafe) return "live";
  if (!status.anythingCanSend && status.gates.some((g) => g.state === "blocked" && g.key !== "sender"))
    return "stopped";
  return "safe";
}

function GatePill({ gate }: { gate: OutreachGate }) {
  return (
    <span
      title={gate.detail}
      className="inline-flex cursor-help items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[gate.state]}`} />
      <span className="text-slate-400">{gate.label}</span>
      <span className="font-medium text-slate-700">{gate.value}</span>
    </span>
  );
}

export function OutreachStatusStrip({ onStatus }: { onStatus?: (s: OutreachStatus) => void }) {
  const { focusedDealerId } = useScope();
  const [status, setStatus] = useState<OutreachStatus | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Which request is current. Switching stores quickly fires overlapping
  // requests, and without this the SLOWER one wins whenever it lands last —
  // leaving the strip describing a rooftop you are no longer looking at. That is
  // how "no campaign is set up" appeared while a store with two was selected.
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    try {
      const params = new URLSearchParams();
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      const res = await apiGet<{ status: OutreachStatus }>(
        `/api/outreach/status?${params.toString()}`
      );
      if (mine !== reqId.current) return; // a newer store was selected; drop this
      setStatus(res.status);
      onStatus?.(res.status);
    } catch (err) {
      // A status strip must never take the page down with it.
      console.error("outreach status load failed:", err);
    }
    // onStatus is intentionally excluded: callers pass inline closures, and
    // re-running on every render would poll the endpoint in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!status) return null;

  const t = tone(status);

  return (
    <div className={`mt-4 rounded-xl border px-4 py-3 ${HEADLINE_TONE[t]}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium">{status.headline}</p>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 text-xs underline underline-offset-2 opacity-70 hover:opacity-100"
        >
          {expanded ? "Hide details" : "What's blocking this?"}
        </button>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {status.gates.map((g) => (
          <GatePill key={g.key} gate={g} />
        ))}
        <span className="ml-1 whitespace-nowrap text-xs text-slate-500">
          {status.totals.pending} waiting · {status.totals.approved} approved
        </span>
      </div>

      {expanded && (
        <dl className="mt-3 space-y-2 border-t border-black/5 pt-3">
          {status.gates.map((g) => (
            <div key={g.key} className="flex gap-2 text-xs">
              <dt className="flex w-36 shrink-0 items-start gap-1.5 font-medium text-slate-700">
                <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${DOT[g.state]}`} />
                {g.label}
              </dt>
              <dd className="text-slate-600">{g.detail}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
